// client/src/coms/components/serversettings_parts/serverSettingsUtils.js
export function safeObj(v) {
  return v && typeof v === 'object' ? v : {};
}

export function safeArr(v) {
  return Array.isArray(v) ? v : [];
}

export function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : fallback;
}

export function toInt01(v) {
  if (v === true) return 1;
  if (v === false) return 0;
  const n = Number(v);
  return n === 1 ? 1 : 0;
}

export function getAppOrigin() {
  try {
    if (typeof window === 'undefined' || !window.location) return '';
    return String(window.location.origin || '').trim();
  } catch {
    return '';
  }
}

export function buildInviteUrl(fullInviteCode) {
  const origin = getAppOrigin();
  const code = String(fullInviteCode || '').trim();
  if (!origin || !code) return '';
  return `${origin}/comms/?invite=${encodeURIComponent(code)}`;
}

export function makeInitials(name) {
  const s = String(name || '').trim();
  if (!s) return 'S';
  const parts = s.split(/\s+/).filter(Boolean);
  if (!parts.length) return 'S';
  const letters = parts.slice(0, 2).map((p) => String(p[0] || '').toUpperCase()).join('');
  return letters || String(s[0] || 'S').toUpperCase();
}

export function normalizeRoleColor(value) {
  const s = String(value || '').trim();
  if (!s) return '';
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s;
  if (/^#[0-9a-fA-F]{3}$/.test(s)) return s;
  return '';
}

export function getColorInputValue(value, fallback = '#5865f2') {
  const n = normalizeRoleColor(value);
  if (!n) return fallback;
  if (/^#[0-9a-fA-F]{3}$/.test(n)) {
    return (
      '#' +
      n[1] + n[1] +
      n[2] + n[2] +
      n[3] + n[3]
    ).toLowerCase();
  }
  return n.toLowerCase();
}

export function normalizeRoleName(value) {
  return String(value || '').replace(/\s+/g, ' ').trim().slice(0, 64).trim();
}

export function normalizePermissionKey(value) {
  return String(value || '').trim().toLowerCase();
}

export function pickServerKey(server) {
  const s = server && typeof server === 'object' ? server : null;
  const v = s && (s.id || s.server_id || s.public_id) ? String(s.id || s.server_id || s.public_id) : '';
  return String(v || '').trim();
}

export function buildFallbackPermissionsMap(serverRolePermissions) {
  const keys = new Set();
  safeArr(serverRolePermissions).forEach((k) => {
    const key = normalizePermissionKey(k);
    if (key) keys.add(key);
  });

  [
    ['manage_messages', 'Manage message-related moderation actions'],
    ['pin_messages', 'Pin and unpin messages'],
    ['set_motd', 'Set and clear message of the day'],
    ['edit_any_message', 'Edit other users messages'],
    ['delete_any_message', 'Delete other users messages'],
    ['manage_channels', 'Create, edit and delete channels'],
    ['manage_roles', 'Create, edit and assign roles'],
    ['manage_server', 'Manage server-level settings'],
    ['kick_members', 'Kick members from the server'],
    ['ban_members', 'Ban members from the server'],
    ['send_messages', 'Send chat messages'],
    ['read_messages', 'Read chat messages'],
    ['join_voice', 'Join voice channels'],
    ['speak_in_voice', 'Speak in voice channels'],
    ['mute_members', 'Mute members in voice'],
    ['deafen_members', 'Deafen members in voice'],
    ['upload_files', 'Upload files and attachments'],
    ['embed_links', 'Embed links in chat'],
    ['mention_everyone', 'Mention everyone or here'],
    ['create_invites', 'Create invite codes'],
    ['manage_invites', 'Manage invite codes'],
    ['create_text_channels', 'Create text channels'],
    ['delete_text_channels', 'Delete text channels'],
    ['create_voice_channels', 'Create voice channels'],
    ['delete_voice_channels', 'Delete voice channels'],
  ].forEach((entry) => keys.add(entry[0]));

  return Array.from(keys).map((key) => ({
    id: 0,
    key,
    description:
      {
        manage_messages: 'Manage message-related moderation actions',
        pin_messages: 'Pin and unpin messages',
        set_motd: 'Set and clear message of the day',
        edit_any_message: 'Edit other users messages',
        delete_any_message: 'Delete other users messages',
        manage_channels: 'Create, edit and delete channels',
        manage_roles: 'Create, edit and assign roles',
        manage_server: 'Manage server-level settings',
        kick_members: 'Kick members from the server',
        ban_members: 'Ban members from the server',
        send_messages: 'Send chat messages',
        read_messages: 'Read chat messages',
        join_voice: 'Join voice channels',
        speak_in_voice: 'Speak in voice channels',
        mute_members: 'Mute members in voice',
        deafen_members: 'Deafen members in voice',
        upload_files: 'Upload files and attachments',
        embed_links: 'Embed links in chat',
        mention_everyone: 'Mention everyone or here',
        create_invites: 'Create invite codes',
        manage_invites: 'Manage invite codes',
        create_text_channels: 'Create text channels',
        delete_text_channels: 'Delete text channels',
        create_voice_channels: 'Create voice channels',
        delete_voice_channels: 'Delete voice channels',
      }[key] || '',
  }));
}

export function roleLabel(role) {
  const r = safeObj(role);
  return String(r.name || 'Role').trim() || 'Role';
}

export function roleColor(role) {
  return normalizeRoleColor(safeObj(role).color);
}

export function sortRoles(list) {
  return safeArr(list).slice().sort((a, b) => {
    const ap = toInt(a && a.position, 0);
    const bp = toInt(b && b.position, 0);
    if (ap !== bp) return bp - ap;
    return toInt(a && a.id, 0) - toInt(b && b.id, 0);
  });
}